import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InstallIcon from './InstallIcon';
import { getInstallGuides, guideKeyForMode } from './installGuides';

/**
 * Step-by-step install sheet for browsers without a one-tap prompt
 * (iOS, Safari on Mac, Android browsers without beforeinstallprompt, and
 * in-app webviews). Bottom sheet on phones, centered dialog on desktop.
 */
export default function InstallSheet({ open, mode, platform, onClose }) {
  const { t } = useTranslation();
  const closeRef = useRef(null);
  const returnFocusRef = useRef(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      returnFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const guide = getInstallGuides(t)[guideKeyForMode(mode)];
  // Safari on iPhone keeps its toolbar (and the Share button) at the bottom.
  const pointDown = mode === 'ios' && platform.iphone;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard blocked in some webviews; steps still apply */ }
  };

  return (
    <div className={`install-sheet-backdrop${pointDown ? ' point-down' : ''}`} onClick={onClose}>
      <div
        className="install-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-sheet-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="install-sheet-head">
          <img src="/apple-touch-icon.png" alt="" className="install-sheet-appicon" />
          <div>
            <h2 id="install-sheet-title" className="install-sheet-title">
              {mode === 'inapp'
                ? guide.title
                : t('download.installSheet.title', 'Add On-Screen to your {{device}}', {
                  device: platform.mobile
                    ? t('download.installSheet.homeScreen', 'home screen')
                    : t('download.installSheet.desktop', 'desktop'),
                })}
            </h2>
            <p className="install-sheet-sub">{t('download.installSheet.sub', 'Free · about 10 seconds · no app store')}</p>
          </div>
          <button ref={closeRef} type="button" className="install-sheet-close" onClick={onClose} aria-label={t('common.close', 'Close')}>
            <InstallIcon name="close" />
          </button>
        </div>

        <ol className="install-steps">
          {guide.steps.map((step, i) => (
            <li key={step.text} className="install-step">
              <span className="install-step-num" aria-hidden="true">{i + 1}</span>
              <span className="install-step-icon"><InstallIcon name={step.icon} /></span>
              <span className="install-step-text">{step.text}</span>
            </li>
          ))}
        </ol>

        {guide.note && <p className="install-sheet-note">{guide.note}</p>}

        {mode === 'inapp' && (
          <button type="button" className="cta-button secondary install-sheet-copy" onClick={copyLink}>
            <InstallIcon name={copied ? 'check' : 'copy'} />
            {copied
              ? t('download.installSheet.copied', 'Link copied. Paste it into your browser')
              : t('download.installSheet.copyLink', 'Copy page link')}
          </button>
        )}

        <button type="button" className="cta-button primary install-sheet-done" onClick={onClose}>
          {t('download.installSheet.gotIt', 'Got it')}
        </button>

        {pointDown && (
          <div className="install-sheet-pointer" aria-hidden="true">
            <InstallIcon name="share" />
            <span>{t('download.installSheet.shareIsHere', 'Share is down here')}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m0 0l-6-6m6 6l6-6" /></svg>
          </div>
        )}
      </div>
    </div>
  );
}
